import { useController, useFormContext } from 'react-hook-form';
import { useMemo } from 'react';

import { processRules } from './utils/processRules';
import { useMarketData } from '@widget/core/MarketProvider';

import type { UseFieldRules } from './utils/processRules';
import type { Modify } from '@toolbox/utils';
import type {
  FieldPath,
  FieldValues,
  UseControllerProps,
  UseControllerReturn,
} from 'react-hook-form';

export type UseFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Modify<
  UseControllerProps<TFieldValues, TFieldName>,
  {
    rules?: UseFieldRules<TFieldValues, TFieldName>;
  }
>;

export const useField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: UseFieldProps<TFieldValues, TName>,
): UseControllerReturn<TFieldValues, TName> => {
  const { getValues } = useFormContext<TFieldValues>();
  const { market } = useMarketData();
  const rules = useMemo(
    () => props.rules && processRules<TFieldValues, TName>(props.rules, { getValues, market }),
    [props.rules, getValues],
  );

  return useController({
    ...props,
    rules,
  });
};
